// -----------------------------
// CartRemoveButton (safe define)
// -----------------------------
if (!window.CartRemoveButton) {
  window.CartRemoveButton = class CartRemoveButton extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('click', (event) => {
        event.preventDefault();
        const cartItems =
          this.closest('cart-items') || this.closest('cart-drawer-items');
        if (cartItems && typeof cartItems.updateQuantity === 'function') {
          cartItems.updateQuantity(this.dataset.index, 0);
        }
      });
    }
  };

  if (
    typeof customElements !== 'undefined' &&
    !customElements.get('cart-remove-button')
  ) {
    customElements.define('cart-remove-button', window.CartRemoveButton);
  }
}

// Shared money formatter for cart.js (uses active Shopify currency)
function formatMoney(cents) {
  const currencyCode = (window.Shopify && Shopify.currency && Shopify.currency.active)
    ? Shopify.currency.active
    : 'GBP';

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode
  }).format(cents / 100);
}


// -----------------------------
// Discount label helper
// -----------------------------
function getDiscountLabel(cart) {
  const titles = [];

  // Cart-level discounts (automatic / codes)
  if (Array.isArray(cart.cart_level_discount_applications)) {
    cart.cart_level_discount_applications.forEach((d) => {
      if (d && d.title) titles.push(d.title);
    });
  }

  // Line-level discounts (fallback)
  if (Array.isArray(cart.items)) {
    cart.items.forEach((item) => {
      if (Array.isArray(item.discounts)) {
        item.discounts.forEach((d) => {
          if (d && d.title && !titles.includes(d.title)) {
            titles.push(d.title);
          }
        });
      }
    });
  }

  return titles.join(', ');
}

// -----------------------------
// Main cart totals
// -----------------------------
function updateCartTotalsUI(cart) {
  const subtotal = document.querySelector('#cart-subtotal');
  const total = document.querySelector('#cart-total');
  const delivery = document.querySelector('#cart-delivery');
  const savings = document.querySelector('#cart-savings');

  // PRE-discount subtotal (before any cart discount)
  if (subtotal) {
    subtotal.innerHTML = `<b>${formatMoney(cart.original_total_price)}</b>`;
  }

  // POST-discount total (what they actually pay)
  if (total) {
    total.textContent = formatMoney(cart.total_price);
  }

  // Shipping threshold logic
  if (delivery) {
    const threshold = window.cartSettings?.freeShippingThreshold || 2500;
    if (cart.total_price >= threshold) {
      delivery.textContent = `FREE 🎉`;
    } else {
      const remaining = threshold - cart.total_price;
      delivery.innerHTML = `<b>${formatMoney(remaining)} away from free shipping</b>`;
    }
  }

  // Savings line
  if (savings) {
    if (cart.total_discount > 0) {
      const discountLabel = getDiscountLabel(cart);
      const amountHtml = `<b>-${formatMoney(cart.total_discount)}</b>`;
      const labelHtml = discountLabel
        ? ` <span class="cart-discount-label">(${discountLabel})</span>`
        : '';

      savings.innerHTML = amountHtml + labelHtml;
    } else {
      savings.innerHTML = `<span class="no-savings">—</span>`;
    }
  }
}


// -----------------------------
// Mini-cart totals (desktop + mobile)
// -----------------------------
function updateMiniCartTotals(cart) {
  // Desktop mini cart elements
  const dSubtotal = document.getElementById('mini-cart-subtotal');
  const dSavings  = document.getElementById('mini-cart-savings');
  const dTotal    = document.getElementById('mini-cart-total');
  const dShip     = document.getElementById('mini-cart-shipping');

  // Mobile mini cart elements
  const mSubtotal = document.getElementById('mobile-cart-subtotal');
  const mSavings  = document.getElementById('mobile-cart-savings');
  const mTotal    = document.getElementById('mobile-cart-total');
  const mShip     = document.getElementById('mobile-cart-shipping');

  // Subtotal (before discount)
  const subtotalText = formatMoney(cart.original_total_price);
  if (dSubtotal) dSubtotal.textContent = subtotalText;
  if (mSubtotal) mSubtotal.textContent = subtotalText;

  // Savings (with discount title)
  if (dSavings || mSavings) {
    if (cart.total_discount > 0) {
      const discountLabel = getDiscountLabel(cart);
      const amountText = `-${formatMoney(cart.total_discount)}`;
      const labelText = discountLabel ? ` (${discountLabel})` : '';

      if (dSavings) {
        dSavings.innerHTML =
          `${amountText} <span class="cart-discount-label">${labelText.trim()}</span>`;
      }
      if (mSavings) {
        mSavings.innerHTML =
          `${amountText} <span class="cart-discount-label">${labelText.trim()}</span>`;
      }
    } else {
      if (dSavings) dSavings.textContent = '—';
      if (mSavings) mSavings.textContent = '—';
    }
  }

  // Total (after discount)
  const totalText = formatMoney(cart.total_price);
  if (dTotal) dTotal.textContent = totalText;
  if (mTotal) mTotal.textContent = totalText;

  // Shipping (same logic as main cart)
  const threshold = window.cartSettings?.freeShippingThreshold || 2500;
  const shippingText =
    cart.total_price >= threshold
      ? 'FREE 🎉'
      : `${formatMoney(threshold - cart.total_price)} away from free shipping`;

  if (dShip) dShip.textContent = shippingText;
  if (mShip) mShip.textContent = shippingText;
}


// -----------------------------
// CartItems web component (guarded)
// -----------------------------
if (
  typeof customElements !== 'undefined' &&
  typeof debounce !== 'undefined' &&
  typeof subscribe !== 'undefined' &&
  typeof PUB_SUB_EVENTS !== 'undefined' &&
  typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined' &&
  typeof fetchConfig !== 'undefined' &&
  !customElements.get('cart-items')
) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      this.lineItemStatusElement =
        document.getElementById('shopping-cart-line-item-status') ||
        document.getElementById('CartDrawer-LineItemStatus');

      const debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, ON_CHANGE_DEBOUNCE_TIMER);

      this.addEventListener('change', debouncedOnChange.bind(this));
    }

    cartUpdateUnsubscriber = undefined;

    connectedCallback() {
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          if (event.source === 'cart-items') {
            return;
          }
          this.onCartUpdate();
        }
      );
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }

    resetQuantityInput(id) {
      const input = this.querySelector(`#Quantity-${id}`);
      if (!input) return;
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    }

    setValidity(event, index, message) {
      event.target.setCustomValidity(message);
      event.target.reportValidity();
      this.resetQuantityInput(index);
      event.target.select();
    }

    validateQuantity(event) {
      const inputValue = parseInt(event.target.value, 10);
      const index = event.target.dataset.index;
      let message = '';

      if (inputValue < event.target.dataset.min) {
        message = window.quickOrderListStrings.min_error.replace(
          '[min]',
          event.target.dataset.min
        );
      } else if (inputValue > parseInt(event.target.max, 10)) {
        message = window.quickOrderListStrings.max_error.replace(
          '[max]',
          event.target.max
        );
      } else if (
        inputValue % parseInt(event.target.step, 10) !== 0
      ) {
        message = window.quickOrderListStrings.step_error.replace(
          '[step]',
          event.target.step
        );
      }

      if (message) {
        this.setValidity(event, index, message);
      } else {
        event.target.setCustomValidity('');
        event.target.reportValidity();
        this.updateQuantity(
          index,
          inputValue,
          document.activeElement.getAttribute('name'),
          event.target.dataset.quantityVariantId
        );
      }
    }

    onChange(event) {
      this.validateQuantity(event);
    }

    onCartUpdate() {
      if (this.tagName === 'CART-DRAWER-ITEMS') {
        fetch(`${routes.cart_url}?sections=cart-drawer`)
          .then((res) => res.json())
          .then((data) => {
            const drawerContent = new DOMParser().parseFromString(
              data['cart-drawer'],
              'text/html'
            );
            const selectors = [
              'cart-drawer-items',
              '.cart-drawer__footer',
            ];
            for (const selector of selectors) {
              const newEl = drawerContent.querySelector(selector);
              const oldEl = document.querySelector(selector);
              if (newEl && oldEl) oldEl.replaceWith(newEl);
            }
          })
          .catch(console.error);
      } else {
        fetch(`${routes.cart_url}?sections=main-cart-items,main-cart-footer`)
          .then((res) => res.json())
          .then((data) => {
            const itemsHTML = new DOMParser().parseFromString(
              data['main-cart-items'],
              'text/html'
            );
            const footerHTML = new DOMParser().parseFromString(
              data['main-cart-footer'],
              'text/html'
            );

            const itemsSection =
              document.querySelector('#main-cart-items');
            const newItems = itemsHTML.querySelector('#main-cart-items');
            if (newItems && itemsSection)
              itemsSection.replaceWith(newItems);

            const newFooterContent = footerHTML.querySelector(
              '#main-cart-footer .js-contents'
            );
            const currentFooterContent = document.querySelector(
              '#main-cart-footer .js-contents'
            );
            if (newFooterContent && currentFooterContent) {
              currentFooterContent.innerHTML =
                newFooterContent.innerHTML;
            }
          })
          .catch(console.error);
      }
    }

    getSectionsToRender() {
      return [
        {
          id: 'main-cart-items',
          section: 'main-cart-items',
          selector: '.js-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: 'cart-live-region-text',
          section: 'cart-live-region-text',
          selector: '.shopify-section',
        },
        {
          id: 'main-cart-footer',
          section: 'main-cart-footer',
          selector: '.js-contents',
        },
      ];
    }

    updateQuantity(line, quantity, name, variantId) {
      this.enableLoading(line);

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map(
          (section) => section.section
        ),
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        ...{ body },
      })
        .then((response) => response.json())
        .then((parsedState) => {
          console.log('parsedState', parsedState);
          const quantityElement =
            document.getElementById(`Quantity-${line}`) ||
            document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            if (quantityElement) {
              quantityElement.value =
                quantityElement.getAttribute('value');
            }
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle(
            'is-empty',
            parsedState.item_count === 0
          );
          const cartDrawerWrapper =
            document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter)
            cartFooter.classList.toggle(
              'is-empty',
              parsedState.item_count === 0
            );
          if (cartDrawerWrapper)
            cartDrawerWrapper.classList.toggle(
              'is-empty',
              parsedState.item_count === 0
            );

          const itemsSection = document.querySelector(
            '#main-cart-items .js-contents'
          );
          if (itemsSection) {
            itemsSection.innerHTML = this.getSectionInnerHTML(
              parsedState.sections['main-cart-items'],
              '.js-contents'
            );
          }

          // Totals
          updateCartTotalsUI(parsedState);
          updateMiniCartTotals(parsedState);

          // Cart icon bubble
          const cartIcon = document.querySelector(
            '#cart-icon-bubble span'
          );
          const cartBubble =
            document.getElementById('cart-icon-bubble');

          if (
            cartIcon &&
            cartBubble &&
            parsedState.item_count != null
          ) {
            cartIcon.textContent = parsedState.item_count;
            cartBubble.classList.add('bump');
            setTimeout(
              () => cartBubble.classList.remove('bump'),
              400
            );
          }

          const updatedValue = parsedState.items[line - 1]
            ? parsedState.items[line - 1].quantity
            : undefined;
          let message = '';
          if (
            items.length === parsedState.items.length &&
            updatedValue !== parseInt(quantityElement.value, 10)
          ) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message =
                window.cartStrings.quantityError.replace(
                  '[quantity]',
                  updatedValue
                );
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) ||
            document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(
                  cartDrawerWrapper,
                  lineItem.querySelector(`[name="${name}"]`)
                )
              : lineItem
                  .querySelector(`[name="${name}"]`)
                  .focus();
          } else if (
            parsedState.item_count === 0 &&
            cartDrawerWrapper
          ) {
            trapFocus(
              cartDrawerWrapper.querySelector('.drawer__inner-empty'),
              cartDrawerWrapper.querySelector('a')
            );
          } else if (
            document.querySelector('.cart-item') &&
            cartDrawerWrapper
          ) {
            trapFocus(
              cartDrawerWrapper,
              document.querySelector('.cart-item__name')
            );
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: parsedState,
            variantId: variantId,
          });
        })
        .catch(() => {
          const errors =
            document.getElementById('cart-errors') ||
            document.getElementById('CartDrawer-CartErrors');
          if (errors) {
            errors.textContent = window.cartStrings.error;
          }
        })
        .finally(() => {
          this.disableLoading(line);
        });
    }

    updateLiveRegions(line, message) {
      const lineItemError =
        document.getElementById(`Line-item-error-${line}`) ||
        document.getElementById(
          `CartDrawer-LineItemError-${line}`
        );
      if (lineItemError) {
        lineItemError.querySelector(
          '.cart-item__error-text'
        ).textContent = message;
      }

      this.lineItemStatusElement.setAttribute('aria-hidden', true);

      const cartStatus =
        document.getElementById('cart-live-region-text') ||
        document.getElementById('CartDrawer-LiveRegionText');
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    enableLoading(line) {
      const mainCartItems =
        document.getElementById('main-cart-items') ||
        document.getElementById('CartDrawer-CartItems');
      if (mainCartItems) {
        mainCartItems.classList.add('cart__items--disabled');
      }

      if (document.activeElement) {
        document.activeElement.blur();
      }
      if (this.lineItemStatusElement) {
        this.lineItemStatusElement.setAttribute('aria-hidden', false);
      }
    }

    disableLoading(line) {
      const mainCartItems =
        document.getElementById('main-cart-items') ||
        document.getElementById('CartDrawer-CartItems');
      if (mainCartItems) {
        mainCartItems.classList.remove('cart__items--disabled');
      }
    }
  }

  customElements.define('cart-items', CartItems);
}

// -----------------------------
// Cart note (same as before, but guarded)
// -----------------------------
if (
  !customElements.get('cart-note') &&
  typeof debounce !== 'undefined' &&
  typeof fetchConfig !== 'undefined'
) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// -----------------------------
// Initialise totals on page load
// -----------------------------
document.addEventListener('DOMContentLoaded', () => {
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      updateCartTotalsUI(cart);
      updateMiniCartTotals(cart);
    })
    .catch(console.error);
});
